import { customColor } from '@brandclub/bc-widgets';
import type { ColorValue, EditField } from '@brandclub/bc-widgets';

/** Widget id the theme editor stores overrides under. */
export const PDP_PRODUCT_CARD_WIDGET_ID = 'concierge-pdp-product-card';

export const PDP_PRODUCT_CARD_DISPLAY_NAME = 'PDP Product Card';

const FONT_WEIGHT_OPTIONS = [
  { label: '400', value: '400' },
  { label: '500', value: '500' },
  { label: '600', value: '600' },
  { label: '700', value: '700' },
];

/**
 * This card reads the **MUI/DTC** theme (`theme.palette.*`), not the widget
 * theme. Those are two independent theme systems, so the color defaults below
 * are deliberately left EMPTY — `buildPdpProductCardStyles` then falls back to
 * the MUI values the card already used, and an unedited brand renders exactly
 * as before. Binding them to `themeColor(...)` would silently repaint the card
 * with widget-theme colors on every already-configured brand.
 *
 * The one intended change: the title and rating fall back to
 * `palette.text.primary` rather than `palette.primary.main`. The accent
 * previously drove both the View button *and* the title, which rendered the
 * title in the button's color on accent-heavy brands.
 */
export const pdpProductCardDefaultState = {
  containerBorderColor: customColor('#dcdddc'),
  containerBorderRadius: 10,
  containerPadding: 16,

  imageSize: 48,

  titleColor: customColor(''),
  titleFontSize: 14,
  titleFontWeight: '600',

  ratingStarColor: customColor('#ED792F'),
  ratingTextColor: customColor(''),
  ratingFontSize: 12,

};

export type PdpProductCardState = typeof pdpProductCardDefaultState;

export const pdpProductCardEditFields: EditField[] = [
  { key: 'containerBorderColor', label: 'Card Border', type: 'color', group: 'Card' },
  { key: 'containerBorderRadius', label: 'Card Radius (px)', type: 'number', min: 0, max: 24, step: 2, group: 'Card' },
  { key: 'containerPadding', label: 'Card Padding (px)', type: 'number', min: 8, max: 32, step: 2, group: 'Card' },
  { key: 'imageSize', label: 'Image Size (px)', type: 'number', min: 32, max: 96, step: 4, group: 'Card' },

  { key: 'titleColor', label: 'Title Color', type: 'color', group: 'Title' },
  { key: 'titleFontSize', label: 'Title Font Size (px)', type: 'number', min: 12, max: 24, step: 1, group: 'Title' },
  { key: 'titleFontWeight', label: 'Title Font Weight', type: 'options', options: [...FONT_WEIGHT_OPTIONS], group: 'Title' },

  { key: 'ratingStarColor', label: 'Rating Star Color', type: 'color', group: 'Rating' },
  { key: 'ratingTextColor', label: 'Rating Text Color', type: 'color', group: 'Rating' },
  { key: 'ratingFontSize', label: 'Rating Font Size (px)', type: 'number', min: 10, max: 18, step: 1, group: 'Rating' },

];

/** Fully resolved values the presentational card renders with. */
export interface PdpProductCardStyles {
  containerBorderColor: string;
  containerBorderRadius: number;
  containerPadding: number;
  imageSize: number;
  titleColor: string;
  titleFontSize: number;
  titleFontWeight: string;
  ratingStarColor: string;
  ratingTextColor: string;
  ratingFontSize: number;
}

type ResolveColor = (value: ColorValue | string | undefined | null, defaultHex?: string) => string;

export const buildPdpProductCardStyles = (
  state: PdpProductCardState,
  resolveColor: ResolveColor,
  /** MUI theme values the card used before it became editable. */
  fallbacks: { textPrimary: string },
): PdpProductCardStyles => {
  // An empty default must land on the MUI fallback, not on an empty string —
  // `resolveColor` passes a `customColor('')` straight through.
  const color = (value: ColorValue | string | undefined | null, fallback: string) =>
    resolveColor(value, fallback) || fallback;

  return {
    containerBorderColor: color(state.containerBorderColor, '#dcdddc'),
    containerBorderRadius: Number(state.containerBorderRadius),
    containerPadding: Number(state.containerPadding),
    imageSize: Number(state.imageSize),
    titleColor: color(state.titleColor, fallbacks.textPrimary),
    titleFontSize: Number(state.titleFontSize),
    titleFontWeight: String(state.titleFontWeight),
    ratingStarColor: color(state.ratingStarColor, '#ED792F'),
    ratingTextColor: color(state.ratingTextColor, fallbacks.textPrimary),
    ratingFontSize: Number(state.ratingFontSize),
  };
};
